import React, { useState, useMemo } from 'react';
import type { TtCourse, TtCourseFormData, LicenseClass, CourseStatus } from '../types';
import { CENTER_ACTIVE_HANG_VALUES as LICENSE_CLASSES } from '../types';

const STATUS_CFG: Record<CourseStatus, { label: string; icon: string; cls: string }> = {
  open: { label: 'Đang mở', icon: 'lock_open', cls: 'open' },
  pending_entry: { label: 'Chờ nhập', icon: 'hourglass_top', cls: 'pending' },
  closed: { label: 'Đã đóng', icon: 'lock', cls: 'closed' },
};

const FILTER_TABS: { key: CourseStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'Tất cả' },
  { key: 'open', label: 'Đang mở' },
  { key: 'closed', label: 'Đã đóng' },
];

type SortKey = 'createdAt' | 'name' | 'hang' | 'recordCount' | 'reviewedCount';

type Props = {
  courses: TtCourse[];
  canWrite: boolean;
  onSelect: (id: number) => void;
  onCreate: (data: TtCourseFormData) => Promise<void>;
  onEdit?: (course: TtCourse) => void;
};

/**
 * Level-1 view of the record management workflow: a table listing every
 * course with full info (status, stats, quota progress, meta, actions).
 * Clicking a row drills into the course's detail page.
 *
 * Replaces the horizontal chip rail (TtCourseBar) for the top-level
 * navigation — the rail hid too many fields per chip; this table shows
 * everything the operator needs to decide which course to work on.
 */
const CourseTableIndex: React.FC<Props> = ({
  courses, canWrite, onSelect, onCreate, onEdit,
}) => {
  const [filterStatus, setFilterStatus] = useState<CourseStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<TtCourseFormData>({ name: '', hang: 'B', isNangHang: false, maxReviewed: null });
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = courses;
    if (filterStatus !== 'all') list = list.filter(c => c.status === filterStatus);
    if (q) list = list.filter(c =>
      c.name.toLowerCase().includes(q)
      || (c.hang || '').toLowerCase().includes(q)
      || (c.creator?.username || '').toLowerCase().includes(q),
    );
    const sorted = [...list].sort((a, b) => {
      let av: number | string = 0; let bv: number | string = 0;
      if (sortKey === 'name') { av = a.name; bv = b.name; }
      else if (sortKey === 'hang') { av = a.hang; bv = b.hang; }
      else if (sortKey === 'recordCount') { av = a.recordCount || 0; bv = b.recordCount || 0; }
      else if (sortKey === 'reviewedCount') { av = a.reviewedCount || 0; bv = b.reviewedCount || 0; }
      else { av = new Date(a.createdAt).getTime(); bv = new Date(b.createdAt).getTime(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [courses, filterStatus, query, sortKey, sortDir]);

  const countByStatus = (s: CourseStatus | 'all') =>
    s === 'all' ? courses.length : courses.filter(c => c.status === s).length;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const sortArrow = (key: SortKey) =>
    sortKey !== key ? '' : sortDir === 'asc' ? '↑' : '↓';

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(form);
      setForm({ name: '', hang: 'B', isNangHang: false, maxReviewed: null });
      setShowForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tt-course-table">
      {/* Header: title + filters + search + add */}
      <div className="tt-course-table__head">
        <div className="tt-course-table__title">
          <i className="material-icons">school</i>
          <span>Quản lý khóa học</span>
          <span className="tt-course-table__count">{courses.length}</span>
        </div>

        <div className="tt-course-table__filters">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              className={`tt-course-table__filter ${filterStatus === tab.key ? 'is-active' : ''}`}
              onClick={() => setFilterStatus(tab.key)}
            >
              {tab.label}<span>{countByStatus(tab.key)}</span>
            </button>
          ))}
        </div>

        <div className="tt-course-table__search">
          <i className="material-icons">search</i>
          <input
            type="text"
            placeholder="Tìm theo tên, hạng, người tạo…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="tt-course-table__search-clear" onClick={() => setQuery('')} aria-label="Xóa">
              <i className="material-icons">close</i>
            </button>
          )}
        </div>

        {canWrite && (
          <button type="button" className="tt-course-table__add" onClick={() => setShowForm(true)}>
            <i className="material-icons">add</i>
            Thêm khóa học
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="tt-course-table__empty">
          <i className="material-icons">inbox</i>
          {courses.length === 0
            ? 'Chưa có khóa học nào. Nhấn "Thêm khóa học" để tạo.'
            : 'Không có khóa nào khớp bộ lọc.'}
        </div>
      ) : (
        <div className="tt-course-table__scroll">
          <table className="tt-course-table__table">
            <thead>
              <tr>
                <th className="is-center" style={{ width: 130 }}>Trạng thái</th>
                <th onClick={() => toggleSort('name')} className="is-sortable">
                  Tên khóa {sortArrow('name')}
                </th>
                <th className="is-center" style={{ width: 108 }}>Nâng hạng</th>
                <th onClick={() => toggleSort('hang')} className="is-sortable is-center" style={{ width: 90 }}>
                  Hạng {sortArrow('hang')}
                </th>
                <th onClick={() => toggleSort('reviewedCount')} className="is-sortable is-center" style={{ width: 110 }}>
                  Đã duyệt {sortArrow('reviewedCount')}
                </th>
                <th className="is-center" style={{ width: 100 }}>Chờ lưu</th>
                <th className="is-center" style={{ width: 124 }}>Nhập CSĐT</th>
                <th className="is-center" style={{ width: 180 }}>Giới hạn duyệt</th>
                <th onClick={() => toggleSort('createdAt')} className="is-sortable is-center" style={{ width: 160 }}>
                  Tạo {sortArrow('createdAt')}
                </th>
                {canWrite && <th style={{ width: 52 }} />}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const cfg = STATUS_CFG[c.status];
                const unreviewed = Math.max(0, (c.recordCount || 0) - (c.reviewedCount || 0));
                const quota = c.maxReviewed;
                const quotaPct = quota && quota > 0
                  ? Math.min(100, Math.round(((c.reviewedCount || 0) / quota) * 100))
                  : null;
                const quotaFull = quota != null && (c.reviewedCount || 0) >= quota;
                return (
                  <tr
                    key={c.id}
                    className={`tt-course-table__row tt-course-table__row--${cfg.cls} ${c.status === 'closed' ? 'is-closed' : ''}`}
                    onClick={() => onSelect(c.id)}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') onSelect(c.id); }}
                  >
                    <td className="is-center">
                      <span className={`tt-course-table__status tt-course-table__status--${cfg.cls}`}>
                        <i className="material-icons">{cfg.icon}</i>
                        {cfg.label}
                      </span>
                    </td>
                    <td>
                      <div className="tt-course-table__name">{c.name}</div>
                    </td>
                    <td className="is-center tt-course-table__nang-hang-cell">
                      {c.isNangHang ? (
                        <i className="material-icons tt-course-table__nang-hang-yes" title="Khóa nâng hạng">check_circle</i>
                      ) : (
                        <span className="tt-course-table__nang-hang-no">—</span>
                      )}
                    </td>
                    <td className="is-center tt-course-table__hang-cell">
                      <span className="tt-course-table__hang">{c.hang}</span>
                    </td>
                    <td className="is-center tt-course-table__ratio-cell">
                      {(() => {
                        const total = c.recordCount ?? 0;
                        const done = c.reviewedCount ?? 0;
                        if (total === 0) {
                          return <span className="tt-course-table__ratio tt-course-table__ratio--muted">0/0</span>;
                        }
                        const complete = done >= total;
                        return (
                          <span
                            className="tt-course-table__ratio"
                            title={`${done} đã duyệt / ${total} hồ sơ${unreviewed > 0 ? ` · còn ${unreviewed} chờ` : ''}`}
                          >
                            <span className="tt-course-table__ratio-num tt-course-table__ratio-num--success">{done}</span>
                            <span className="tt-course-table__ratio-sep">/</span>
                            <span className={`tt-course-table__ratio-num ${complete ? 'tt-course-table__ratio-num--success' : 'tt-course-table__ratio-num--warn'}`}>{total}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="is-center tt-course-table__pending-cell">
                      {(() => {
                        const pending = c.pendingCount ?? 0;
                        if (pending === 0) {
                          return <span className="tt-course-table__pending tt-course-table__pending--none">—</span>;
                        }
                        return (
                          <span className="tt-course-table__pending" title="Số hồ sơ đang chờ lưu vào khóa">
                            <i className="material-icons">pending_actions</i>
                            {pending}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="is-center tt-course-table__csdt-cell">
                      {(() => {
                        const total = c.reviewedCount ?? 0;
                        const success = c.enteredCsdtCount ?? 0;
                        const error = c.csdtFailedCount ?? 0;
                        if (total === 0) {
                          return <span className="tt-course-table__ratio tt-course-table__ratio--muted">—</span>;
                        }
                        const complete = success >= total;
                        return (
                          <span
                            className="tt-course-table__ratio"
                            title={`${success} thành công / ${total} đã duyệt${error > 0 ? ` / ${error} lỗi` : ''}`}
                          >
                            <span className="tt-course-table__ratio-num tt-course-table__ratio-num--success">{success}</span>
                            <span className="tt-course-table__ratio-sep">/</span>
                            <span className={`tt-course-table__ratio-num ${complete ? 'tt-course-table__ratio-num--success' : 'tt-course-table__ratio-num--warn'}`}>{total}</span>
                            {error > 0 && (
                              <>
                                <span className="tt-course-table__ratio-sep">/</span>
                                <span className="tt-course-table__ratio-num tt-course-table__ratio-num--danger">{error}</span>
                              </>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="is-center">
                      {quota != null ? (
                        <div className={`tt-course-table__quota${quotaFull ? ' is-full' : ''}`}>
                          <div className="tt-course-table__quota-label">
                            {c.reviewedCount ?? 0}/{quota} · {quotaPct}%
                          </div>
                          <div className="tt-course-table__quota-bar">
                            <div className="tt-course-table__quota-fill" style={{ width: `${quotaPct}%` }} />
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.78rem' }}>— không đặt —</span>
                      )}
                    </td>
                    <td className="is-center">
                      <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{new Date(c.createdAt).toLocaleDateString('vi-VN')}</div>
                      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 2 }}>
                        <i className="material-icons" style={{ fontSize: 11, verticalAlign: 'middle' }}>person</i>
                        {' '}{c.creator?.username || '—'}
                      </div>
                    </td>
                    {canWrite && onEdit && (
                      <td className="tt-course-table__action-cell" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="tt-course-table__action-btn"
                          onClick={() => onEdit(c)}
                          title="Sửa khóa"
                        >
                          <i className="material-icons">edit</i>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showForm && canWrite && (
        <div className="tt-course-bar__modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="tt-course-bar__modal">
            <div className="tt-course-bar__modal-head">
              <i className="material-icons">add_circle</i>
              <h3>Thêm khóa học mới</h3>
              <button type="button" className="tt-course-bar__modal-close" onClick={() => setShowForm(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="tt-course-bar__modal-body">
              <label>
                Tên khóa học
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="VD: Khóa 2026-04 B1"
                  autoFocus
                />
              </label>
              <label>
                Hạng GPLX
                <select
                  value={form.hang}
                  onChange={(e) => setForm({ ...form, hang: e.target.value as LicenseClass })}
                >
                  {LICENSE_CLASSES.map(h => <option key={h} value={h}>Hạng {h}</option>)}
                </select>
              </label>
              <label>
                Giới hạn số hồ sơ được duyệt
                <input
                  type="number"
                  min={0}
                  placeholder="Để trống = không giới hạn"
                  value={form.maxReviewed ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({ ...form, maxReviewed: v === '' ? null : Math.max(0, parseInt(v, 10) || 0) });
                  }}
                />
              </label>
              <label className="tt-course-bar__modal-check">
                <input
                  type="checkbox"
                  checked={!!form.isNangHang}
                  onChange={(e) => setForm({ ...form, isNangHang: e.target.checked })}
                />
                Khóa nâng hạng
              </label>
            </div>
            <div className="tt-course-bar__modal-foot">
              <button type="button" className="tt-btn tt-btn--outline" onClick={() => setShowForm(false)}>Hủy</button>
              <button
                type="button"
                className="tt-btn tt-btn--primary"
                disabled={submitting || !form.name.trim()}
                onClick={handleCreate}
              >
                {submitting ? 'Đang tạo…' : 'Tạo khóa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseTableIndex;
